import { off } from "process"
import { Product } from "../../../checkout/product"
import { findProductBySku } from "../../../hooks/useProducts"
import { formatCurrencyString } from "../../../utils/format"
import { LineItem, Subscription, SubscriptionData } from "./subscription"

export interface FeatureBilling {
  productName: string
  currentPeriodValue?: number
  currentPeriodLimit?: number
  pricePerUnit: number
  numPeriodUpfront: number
  upfrontPeriodsRemaining: number
  numPeriodNoLimit: number
  noLimitPeriodsRemaining: number
  priceId?: string
  secondaryPriceId?: string
  usageChargeDescription?: string
  priceStructure?: string
  tiers: { upTo?: number; price: number }[]
}
export interface MembershipProps {
  status: "active" | "inactive" | "trialing"
  endDate: {
    formatted: string
  }
  subscription: SubscriptionData
  featureBilling: FeatureBilling[]
}

export class Membership {
  status: "active" | "inactive" | "trialing"
  endDate: {
    formatted: string
  }
  subscription: Subscription
  featureBilling: FeatureBilling[]

  constructor(props: MembershipProps) {
    this.endDate = props.endDate
    this.status = props.status
    this.subscription = new Subscription(props.subscription)
    this.featureBilling = props.featureBilling
    this.featureBilling.forEach(f => {
      if (f.priceStructure) {
        const ps = JSON.parse(f.priceStructure)
        f.tiers = ps.tiers
      }
    })
  }

  isProfesssional() {
    return this.subscription.isProfesssional()
  }

  isPersonal() {
    return this.subscription.isPersonal()
  }

  getPriceQuanty(lineItem?: LineItem): number {
    if (!lineItem?.priceId) {
      return lineItem?.quantity ?? 0
    }

    const isPrimary = this.isPrimaryFeature(lineItem.priceId)

    const feature = isPrimary
      ? this.getPrimaryFeatureUsage(lineItem.priceId)
      : this.getSecondaryFeatureUsage(lineItem.priceId)

    if (!feature) {
      return lineItem?.quantity ?? 0
    }

    if (feature.noLimitPeriodsRemaining > 0) {
      return 0
    }
    if (isPrimary && this.subscription.cancelAtPeriodEnd) {
      return 0
    }
    if (
      !isPrimary &&
      this.subscription.contracts.length > 0 &&
      feature.used < this.subscription.contracts[0].minUsage
    ) {
      return this.subscription.contracts[0].minUsage - feature.included
    }

    return feature.billed
  }

  getUnitAmount(lineItem?: LineItem): number {
    if (!lineItem?.priceId) {
      return lineItem?.unitAmount ?? 0
    }

    const featurePrice = this.featureBilling.find(
      f => f.secondaryPriceId === lineItem.priceId
    )

    if (!featurePrice) {
      return lineItem?.unitAmount ?? 0
    }

    if (featurePrice.pricePerUnit > 0) {
      return featurePrice.pricePerUnit
    }

    return lineItem?.unitAmount ?? 0
  }

  isPrimaryFeature(priceId?: string): boolean {
    if (!this.featureBilling || this.featureBilling.length <= 0) {
      return false
    }
    return this.featureBilling.some(f => f.priceId === priceId)
  }

  isSecondaryFeature(priceId?: string): boolean {
    if (!this.featureBilling || this.featureBilling.length <= 0) {
      return false
    }
    return this.featureBilling.some(f => f.secondaryPriceId === priceId)
  }

  getPrimaryFeatureUsage(
    priceId?: string
  ): {
    included: number
    used: number
    remaining: number
    billed: number
    noLimitPeriodsRemaining: number
  } | null {
    if (!this.featureBilling || this.featureBilling.length <= 0) {
      return null
    }

    const res = {
      included: 0,
      used: 0,
      remaining: 0,
      billed: 0,
      noLimitPeriodsRemaining: 0,
    }
    const featurePrice = this.featureBilling.find(f => f.priceId === priceId)
    if (featurePrice) {
      res.noLimitPeriodsRemaining = featurePrice.upfrontPeriodsRemaining ?? 0
      res.remaining = res.noLimitPeriodsRemaining
      res.included = featurePrice.numPeriodUpfront ?? 0
      res.used = 1

      res.billed = res.remaining === 0 ? 1 : 0
    }

    return res
  }

  getSecondaryFeatureUsage(
    priceId?: string
  ): {
    included: number
    used: number
    remaining: number
    billed: number
    noLimitPeriodsRemaining: number
    usageChargeDescription?: string
  } | null {
    if (!this.featureBilling || this.featureBilling.length <= 0) {
      return null
    }
    const res = {
      included: 0,
      used: 0,
      remaining: 0,
      billed: 0,
      noLimitPeriodsRemaining: 0,
      usageChargeDescription: "",
    }

    const featurePrice = this.featureBilling.find(
      f => f.secondaryPriceId === priceId
    )
    if (featurePrice) {
      res.usageChargeDescription = featurePrice.usageChargeDescription ?? ""
      res.noLimitPeriodsRemaining = featurePrice.noLimitPeriodsRemaining ?? 0
      res.included = featurePrice.currentPeriodLimit ?? 0
      res.used = featurePrice.currentPeriodValue ?? 0
      res.remaining = Math.max(res.included - res.used, 0)
      res.billed =
        res.used > 0 && res.remaining === 0 ? res.used - res.included : 0
    }

    return res
  }

  buildLineItemDescription(lineItem: LineItem): string {
    return `${lineItem.description}`
  }
  buildLineItemSubDescription(lineItem: LineItem): string {
    if (this.isPrimaryFeature(lineItem.priceId)) {
      const featureUsage = this.getPrimaryFeatureUsage(lineItem.priceId)

      return (featureUsage?.noLimitPeriodsRemaining ?? 0) > 0
        ? `${formatCurrencyString({
            value: lineItem.unitAmount,
            currency: lineItem.currency.toUpperCase(),
          })} pre-paid`
        : this.subscription.cancelAtPeriodEnd
        ? "Canceled"
        : ""
    }

    if (this.isSecondaryFeature(lineItem.priceId)) {
      const featurePrice = this.featureBilling.find(
        f => f.secondaryPriceId === lineItem.priceId
      )
      if (featurePrice?.usageChargeDescription) {
        return featurePrice.usageChargeDescription
      }
    }
    return `${lineItem.description}`
  }

  getNumIncluded(lineItem: LineItem): number {
    const usage = this.getSecondaryFeatureUsage(lineItem.priceId)
    if (!usage) {
      return 0
    }
    return usage.included
  }

  getPriceTiers(
    lineItem: LineItem
  ): { amount: number; limit?: number; start: number }[] {
    const featurePrice = this.featureBilling.find(
      f => f.secondaryPriceId === lineItem.priceId
    )
    return this.buildPriceTiers(featurePrice)
  }

  buildPriceTiers(
    featureBilling?: FeatureBilling
  ): { amount: number; limit?: number; start: number }[] {
    const res: { amount: number; limit?: number; start: number }[] = []
    if (!featureBilling || !featureBilling.priceStructure) {
      return res
    }
    let start = 1
    const incluedAmt = featureBilling.currentPeriodLimit ?? 0
    if (incluedAmt > 0) {
      //   res.push({ amount: 0, limit: incluedAmt, start })
      start = incluedAmt + 1
    }
    featureBilling.tiers.forEach(t => {
      res.push({
        amount: t.price,
        limit: t.upTo ? t.upTo + incluedAmt : undefined,
        start,
      })
      start = t.upTo ? t.upTo + 1 + incluedAmt : start
    })
    return res
  }

  validateProContractOffer(
    offer: { featureSkus: string[]; minUsage: number; discountPercent: number },
    products: Product[]
  ): { valid: boolean; minCharge?: number; minChargeWithMembership?: number } {
    const hasContract = this.subscription.contracts.find(c =>
      c.featureSkus.some(sku => offer.featureSkus.includes(sku))
    )
    if (hasContract) {
      return { valid: false }
    }
    //IMPORTANT EXPECTS ONLY 1 usage product
    let minCharge = 0
    let membershipCharge = 0
    offer.featureSkus.forEach(s => {
      const p = findProductBySku(products, s)
      if (p?.isUsage()) {
        const priceId = p.getPrice(this.subscription.currency as any)?.id
        const fb = this.featureBilling.find(
          fb => fb.secondaryPriceId === priceId
        )
        if (fb) {
          this.buildPriceTiers(fb).forEach(t => {
            if (
              t.start <= offer.minUsage &&
              (!t.limit || t.limit >= offer.minUsage)
            ) {
              minCharge =
                (offer.minUsage - (fb.currentPeriodLimit ?? 0)) *
                t.amount *
                (1 - offer.discountPercent)
            }
          })
        }
      } else {
        const amt = p?.getPriceAmt(this.subscription.currency as any) ?? 0
        membershipCharge += amt * (1 - offer.discountPercent)
      }
    })
    return {
      valid: true,
      minCharge,
      minChargeWithMembership: minCharge + membershipCharge,
    }
  }

  estimateCostProContractOffer(
    offer: { featureSkus: string[]; minUsage: number; discountPercent: number },
    products: Product[],
    numSessions: number
  ): {
    usageCharge: number
    chargeWithMembership: number
    costPerSession: number
    membershipCharge: number
    highestTier: number
    includedSessions: number
    discountAmt: number
  } {
    //IMPORTANT EXPECTS ONLY 1 usage product
    let usageCharge = 0
    let membershipCharge = 0
    let highestTier = 0
    let discountAmt = 0
    let includedSessions = 0
    if (numSessions < offer.minUsage) {
      numSessions = offer.minUsage
    }
    offer.featureSkus.forEach(s => {
      const p = findProductBySku(products, s)
      if (p?.isUsage()) {
        const priceId = p.getPrice(this.subscription.currency as any)?.id
        const fb = this.featureBilling.find(
          fb => fb.secondaryPriceId === priceId
        )

        if (fb) {
          includedSessions = fb.currentPeriodLimit ?? 0
          this.buildPriceTiers(fb).forEach(t => {
            highestTier =
              t.limit && t.limit! > highestTier ? t.limit : highestTier
            if (
              t.start <= numSessions &&
              (!t.limit || t.limit >= numSessions)
            ) {
              usageCharge =
                (numSessions - includedSessions) *
                t.amount *
                (1 - offer.discountPercent)
              discountAmt +=
                (numSessions - includedSessions) *
                t.amount *
                offer.discountPercent
            }
          })
        }
      } else {
        const amt = p?.getPriceAmt(this.subscription.currency as any) ?? 0
        membershipCharge += amt * (1 - offer.discountPercent)
        discountAmt += amt * offer.discountPercent
      }
    })

    return {
      usageCharge,
      chargeWithMembership: usageCharge + membershipCharge,
      costPerSession: Math.round(
        (usageCharge + membershipCharge) / numSessions
      ),
      membershipCharge,
      highestTier,
      includedSessions,
      discountAmt,
    }
  }
}
